/**
 * App Container
 */

import { QueryStringContextProvider } from '../contexts/queryString/provider';
import { setAuthenticationConfig } from '../redux/actions/authentication';
import { setProjects } from '../redux/actions/projects';
import { setStaticData } from '../redux/actions/staticData';
import { requestTrackUserRegistration, setUserLoginStatus, setUserProfile, setViewerId } from '../redux/actions/user';
import { initialState as staticDataInitialState } from '../redux/reducers/staticData';
import { AppFragment } from '../relay/app';
import { getProjectsFromViewer } from '../relay/selectors/projects';

import { WithRouter } from 'found';
import { isEqual } from 'lodash';
import * as React from 'react';
import { connect, DispatchProp } from 'react-redux';
import { createFragmentContainer } from 'react-relay';

import AppWrapper from '../components/organisms/global/AppWrapper';
import { CookieContextProvider } from '../contexts/cookie/provider';
import { CurrencyContextProvider } from '../contexts/currency/provider';
import { DownloadContextProvider } from '../contexts/download/provider';
import { OnboardingContextProvider } from '../contexts/onboarding/provider';
import { TrackLicensingContextProvider } from '../contexts/trackLicensing/provider';
import { activateOptimize } from '../utils/google-optimize';

interface IOwnStateProps {
  projects: GQL.IProject[];
}

interface IOwnProps extends WithRouter {
  children?: React.ReactNode[];
  main?: React.ReactNode;
  viewer: GQL.IViewer;
}

type AppProps = IOwnProps & IOwnStateProps & Required<DispatchProp<any>>;

const defaultList = [];

class App extends React.Component<AppProps> {

  constructor(props) {
    super(props);

    this.hydrateState();
  }

  componentDidMount() {
    // this.enableWhyDidYouUpdate();
    activateOptimize();
  }

  componentDidUpdate(prevProps: AppProps) {
    if (prevProps.viewer.profile !== this.props.viewer.profile) {
      this.props.dispatch(setUserLoginStatus(!!this.props.viewer.profile));
    }

    if (!isEqual(prevProps.projects, this.props.projects)) {
      this.hydrateProjects();
    }
  }

  render() {
    const { children, main, viewer } = this.props;
    return (
      <QueryStringContextProvider>
        <CurrencyContextProvider viewer={viewer}>
          <TrackLicensingContextProvider>
            <CookieContextProvider>
              <DownloadContextProvider>
                <OnboardingContextProvider>
                  <AppWrapper
                    cartInfo={viewer.cart}
                    projects={this.getProjectsFromViewer()}
                    userProfile={viewer.profile}
                    viewer={viewer}
                  >{main || children}
                  </AppWrapper>
                </OnboardingContextProvider>
                </DownloadContextProvider>
            </CookieContextProvider>
          </TrackLicensingContextProvider>
        </CurrencyContextProvider>
      </QueryStringContextProvider>
    );
  }

  // @ts-ignore
  private enableWhyDidYouUpdate() {
    if (process.env.NODE_ENV !== 'production') {
      const { whyDidYouUpdate } = require('why-did-you-update');
      whyDidYouUpdate(React);
    }
  }

  private hydrateState() {
    this.hydrateUserState();
    this.hydrateStaticData();
    this.hydrateAuthenticationConfig();
  }

  private hydrateUserState() {
    const { dispatch, viewer } = this.props;

    const isUserLoggedIn = !!viewer.profile;

    dispatch(setUserLoginStatus(isUserLoggedIn));
    dispatch(setViewerId({ viewerId: viewer.id }));

    if (isUserLoggedIn) {
      dispatch(setUserProfile({ profile: viewer.profile! }));
      this.hydrateProjects();
    }

    if (viewer.profile && !viewer.profile.ga_account_goal_sent) {
      const promise = new Promise((resolve, reject) => {
        dispatch(requestTrackUserRegistration({
          viewerId: viewer.id,
          promise: { resolve, reject },
        }));
      });
      promise.then((mutationResult: any) => {
        const payload = mutationResult.updateProfile as GQL.IUpdateProfilePayload;
        dispatch(
          setUserProfile({ profile: payload.profile as any }),
        );
      });
    }
  }

  private hydrateProjects({ dispatch, viewer } = this.props) {
    const projects = this.getProjectsFromViewer(viewer);
    dispatch(setProjects({ projects }));
  }

  private hydrateStaticData({ dispatch, viewer } = this.props) {
    const staticDataState: IStaticDataState = staticDataInitialState;

    if (viewer.taxonomies) {
      staticDataState.taxonomies = viewer.taxonomies.edges!.map(({ node }) => node!);
    }
    if (viewer.tempos) {
      staticDataState.tempos = viewer.tempos.edges!.map(({ node }) => node!);
    }
    if (viewer.license_components) {
      staticDataState.licenseComponents = viewer.license_components;
    }
    if (viewer.business_information) {
      staticDataState.businessInformation = viewer.business_information!.ams;
    }
    if (viewer.latest_exchange_rates) {
      staticDataState.exchangeRates = viewer.latest_exchange_rates;
    }

    dispatch(setStaticData(staticDataState));
  }

  private hydrateAuthenticationConfig({ dispatch, viewer } = this.props) {
    if (viewer.authentication_info) {
      dispatch(setAuthenticationConfig(viewer.authentication_info!));
    }
  }

  private getProjectsFromViewer(viewer = this.props.viewer) {
    return viewer.projects ? viewer.projects.edges!.map(({ node }) => node!) : defaultList;
  }
}

const ConnectedApp = connect<IOwnStateProps, DispatchProp<any>, IOwnProps, IStoreState>(
  (_, ownProps) => ({
    projects: getProjectsFromViewer(ownProps),
  }),
  (dispatch) => ({ dispatch }),
)(App);

const AppWithRelay = createFragmentContainer(
  ConnectedApp,
  AppFragment,
);

export default AppWithRelay;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/containers/App.tsx